/**
 * Pre-order "buy now" — skip the cart, go straight to checkout.
 *
 * The redirect is gated on two things, and both have to be true:
 *
 *   1. Products tagged `preorder` (or `pre-order`) get a `preorder-checkout`
 *      attribute on their <product-form> wrapper (see buy-buttons.liquid). That
 *      is the merchant's product-level opt in.
 *   2. The selected variant is actually on pre-order. Pre-order is per variant,
 *      not per product, so a tagged product can have variants that are just
 *      normal stock. The Notify Me app (block `notify_me_preorder_selling_plan`
 *      in templates/product.json) is the source of truth here: it adds the
 *      `hengam-preorder-button` class to the add to cart <button> when the
 *      selected variant is a pre-order, and takes it off again when it is not.
 *
 * Both are read at submit time, so switching variants needs no bookkeeping on
 * our side — whatever the app has done to the button by the time the customer
 * clicks is what we act on. If the class is absent we return before touching the
 * event, so <product-form> handles it as an ordinary add to cart.
 *
 * Why intercept the form rather than the button:
 *
 * The theme's <product-form> element binds a `submit` listener to the form and
 * turns the submit into an AJAX `/cart/add.js` call that opens the drawer, so a
 * hidden `return_to=/checkout` input would never be read. A capture-phase
 * `submit` listener on the document runs before any target-phase listener on the
 * form, so `stopPropagation()` here means <product-form> never sees the event.
 *
 * Marking the form (not the button) covers every route into it in one go: the
 * main add to cart button, the sticky add to cart bar — whose buttons live
 * outside the form and are linked by their `form` attribute — and an Enter
 * keypress in one of the fields.
 *
 * The form is read at submit time, so the selected variant, quantity, any line
 * item properties and the selling plan all come along automatically. Existing
 * cart items are left alone: this adds the pre-order and goes to checkout, it
 * does not empty the basket first.
 *
 * Listening on the document also means this survives <product-rerender> swapping
 * the markup on a variant change, and works for quick-buy drawers whose content
 * is cloned in from a fetched product page.
 */
(function () {
  "use strict";

  const FORM_SELECTOR = 'product-form[preorder-checkout] form[action*="/cart/add"]';

  // Owned by the Notify Me app, not by us. If the app ever renames it the gate
  // below stops matching and pre-orders fall back to a normal add to cart.
  const PREORDER_BUTTON_CLASS = "hengam-preorder-button";

  /**
   * Locale-aware root, e.g. "/" or "/en-gb/". Shopify.routes is set by the
   * platform, but guard in case it is missing.
   */
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function showError(message) {
    document.dispatchEvent(
      new CustomEvent("toast:show", {
        detail: { message: message, tone: "error" }
      })
    );
  }

  /** Every submit button bound to this form, including the sticky bar's. */
  function submitButtons(form) {
    return Array.from(form.elements).filter((element) => element.type === "submit");
  }

  /**
   * Is the variant currently selected in this form on pre-order?
   *
   * The app only marks the main add to cart button, so this asks whether *any*
   * button bound to the form carries the class rather than looking at the one
   * that was clicked. That way the sticky add to cart bar — whose buttons the
   * app does not touch — follows the main button instead of always missing.
   */
  function isPreorderVariant(form) {
    return submitButtons(form).some((button) => button.classList.contains(PREORDER_BUTTON_CLASS));
  }

  async function onSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) {
      return;
    }

    // Return *before* touching the event: a non pre-order variant has to reach
    // <product-form> untouched so it adds to the cart the normal way.
    if (!isPreorderVariant(form)) {
      return;
    }

    event.preventDefault();

    // Stop the event reaching <product-form>'s own submit handler.
    event.stopPropagation();

    // Matches <product-form>'s guard: ignore repeat clicks while in flight.
    if (event.submitter && event.submitter.getAttribute("aria-busy") === "true") {
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const buttons = submitButtons(form);

    buttons.forEach((button) => button.setAttribute("aria-busy", "true"));

    try {
      const response = await fetch(`${root()}cart/add.js`, {
        body: new FormData(form),
        method: "POST",
        headers: {
          // Needed for Shopify to check inventory before accepting the line.
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      if (response.ok) {
        // Leave the buttons busy so the loader stays on while we navigate away.
        window.location.href = `${root()}checkout`;
        return;
      }

      const error = await response.json();

      buttons.forEach((button) => button.removeAttribute("aria-busy"));
      showError(error.description || error.message);

      // Cart state may have moved on (e.g. sold out), so resync the drawer.
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    } catch (requestError) {
      // Network or parse failure. The theme has no locale string for this case,
      // so the copy lives here — move it into locales/*.json if the store ever
      // goes multi-language.
      buttons.forEach((button) => button.removeAttribute("aria-busy"));
      showError("Sorry, we couldn't start your pre-order. Please try again.");
    }
  }

  document.addEventListener("submit", onSubmit, { capture: true });
})();
